/**
 * SingleSelect — the tag pill above an option's label (web only).
 */

/// <reference lib="dom" />

import React from 'react';
import type {
  OptionTag as OptionTagValue,
  OptionTagTone,
  SingleSelectOption,
} from './SingleSelect';
import { optionAccessibilityLabel } from './optionFeedback';
import { Icon } from '../Icon/Icon.web';
import { colors } from '../../elements/colors';
import { radius } from '../../elements/radius';
import { spacing } from '../../elements/spacing';
import { typography } from '../../elements/typography';

const TAG_TYPOGRAPHY = typography.c2Regular;
const TAG_ICON_SIZE = TAG_TYPOGRAPHY.fontSize;

// Figma v9 5.1a "Recommended" and 5.1 points pills.
const TAG_BACKGROUND: Record<OptionTagTone, string> = {
  info: colors.background.callout,
  accent: colors.punchPink['200'],
};

/**
 * The option's accessible name on web: the shared name, followed by the tag
 * so the offer is announced with the option. Native keeps the shared name.
 */
export function webOptionAccessibilityLabel(option: SingleSelectOption): string {
  const name = optionAccessibilityLabel(option);
  if (option.accessibilityLabel !== undefined || option.tag === undefined) return name;
  return `${name}, ${option.tag.label}`;
}

/** Visual only: the option button's name already carries the tag. */
export function OptionTag({
  tag,
  'data-testid': testId,
}: {
  tag: OptionTagValue;
  'data-testid'?: string;
}) {
  const style: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: spacing.xs,
    padding: `${spacing.xxs}px ${spacing.s}px`,
    borderRadius: radius.sm,
    backgroundColor: TAG_BACKGROUND[tag.tone],
    color: colors.text.primary,
    fontFamily: TAG_TYPOGRAPHY.fontFamily,
    fontWeight: TAG_TYPOGRAPHY.fontWeight,
    fontSize: TAG_TYPOGRAPHY.fontSize,
    lineHeight: `${TAG_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: TAG_TYPOGRAPHY.letterSpacing,
  };
  return (
    <span style={style} aria-hidden="true" data-tone={tag.tone} data-testid={testId}>
      {tag.icon ? <Icon name={tag.icon} size={TAG_ICON_SIZE} color={colors.text.primary} /> : null}
      {tag.label}
    </span>
  );
}
